import { createApiRequest } from "@/lib/api/createApiService";
import { SURVEYS_ENDPOINTS } from "./surveys.endpoints";

export const surveysService = {
  fetchLookups: async (options = {}) => {
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.LOOKUPS,
      method: "GET",
      ...options,
    });
  },

  listStandard: async (options = {}) => {
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.STANDARD,
      method: "GET",
      ...options,
    });
  },

  listCustom: async (options = {}) => {
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.CUSTOM,
      method: "GET",
      ...options,
    });
  },

  listResponses: async (params = {}, options = {}) => {
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.RESPONSES,
      method: "GET",
      body: params,
      ...options,
    });
  },

  getResponseDetail: async (responseId, options = {}) => {
    if (!responseId) throw new Error("responseId is required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.RESPONSE_DETAIL(responseId),
      method: "GET",
      ...options,
    });
  },

  getStandardSurvey: async (surveyId, options = {}) => {
    if (!surveyId) throw new Error("surveyId is required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.STANDARD_DETAIL(surveyId),
      method: "GET",
      ...options,
    });
  },

  getCustomSurvey: async (surveyId, options = {}) => {
    if (!surveyId) throw new Error("surveyId is required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.CUSTOM_DETAIL(surveyId),
      method: "GET",
      ...options,
    });
  },

  createStandardSurvey: async (payload, options = {}) => {
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.STANDARD,
      method: "POST",
      body: payload,
      ...options,
    });
  },

  updateStandardSurvey: async (surveyId, payload, options = {}) => {
    if (!surveyId) throw new Error("surveyId is required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.STANDARD_DETAIL(surveyId),
      method: "PATCH",
      body: payload,
      ...options,
    });
  },

  deleteStandardSurvey: async (surveyId, options = {}) => {
    if (!surveyId) throw new Error("surveyId is required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.STANDARD_DETAIL(surveyId),
      method: "DELETE",
      ...options,
    });
  },

  publishStandardSurvey: async (surveyId, options = {}) => {
    if (!surveyId) throw new Error("surveyId is required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.STANDARD_PUBLISH(surveyId),
      method: "POST",
      body: {},
      ...options,
    });
  },

  archiveStandardSurvey: async (surveyId, options = {}) => {
    if (!surveyId) throw new Error("surveyId is required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.STANDARD_ARCHIVE(surveyId),
      method: "POST",
      body: {},
      ...options,
    });
  },

  createCustomSurvey: async (payload, options = {}) => {
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.CUSTOM,
      method: "POST",
      body: payload,
      ...options,
    });
  },

  updateCustomSurvey: async (surveyId, payload, options = {}) => {
    if (!surveyId) throw new Error("surveyId is required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.CUSTOM_DETAIL(surveyId),
      method: "PATCH",
      body: payload,
      ...options,
    });
  },

  deleteCustomSurvey: async (surveyId, options = {}) => {
    if (!surveyId) throw new Error("surveyId is required");
    await createApiRequest({
      url: SURVEYS_ENDPOINTS.CUSTOM_DETAIL(surveyId),
      method: "DELETE",
      ...options,
    });
    return { success: true };
  },

  publishCustomSurvey: async (surveyId, options = {}) => {
    if (!surveyId) throw new Error("surveyId is required");
    await createApiRequest({
      url: SURVEYS_ENDPOINTS.CUSTOM_PUBLISH(surveyId),
      method: "POST",
      body: {},
      ...options,
    });
    return { success: true };
  },

  archiveCustomSurvey: async (surveyId, options = {}) => {
    if (!surveyId) throw new Error("surveyId is required");
    await createApiRequest({
      url: SURVEYS_ENDPOINTS.CUSTOM_ARCHIVE(surveyId),
      method: "POST",
      body: {},
      ...options,
    });
    return { success: true };
  },

  shareSurvey: async (surveyType, surveyId, payload, options = {}) => {
    if (!surveyType || !surveyId)
      throw new Error("surveyType and surveyId are required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.SHARE(surveyType, surveyId),
      method: "POST",
      body: payload,
      ...options,
    });
  },

  inviteSurvey: async (surveyType, surveyId, payload, options = {}) => {
    if (!surveyType || !surveyId)
      throw new Error("surveyType and surveyId are required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.INVITE(surveyType, surveyId),
      method: "POST",
      body: payload,
      ...options,
    });
  },

  resolveInviteEmails: async (surveyType, surveyId, payload, options = {}) => {
    if (!surveyType || !surveyId)
      throw new Error("surveyType and surveyId are required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.INVITE_RESOLVE(surveyType, surveyId),
      method: "POST",
      body: payload,
      ...options,
    });
  },

  fetchInviteSuggestions: async (surveyType, surveyId, query, options = {}) => {
    if (!surveyType || !surveyId)
      throw new Error("surveyType and surveyId are required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.INVITE_SUGGESTIONS(surveyType, surveyId, query),
      method: "GET",
      ...options,
    });
  },

  fetchSharedSurvey: async (surveyType, surveyId, params, options = {}) => {
    if (!surveyType || !surveyId)
      throw new Error("surveyType and surveyId are required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.SHARED(surveyType, surveyId),
      method: "GET",
      body: params,
      ...options,
    });
  },

  fetchAvailableSurveys: async (options = {}) => {
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.AVAILABLE,
      method: "GET",
      ...options,
    });
  },

  listQuestionBank: async (params = {}, options = {}) => {
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.QUESTION_BANK,
      method: "GET",
      body: params,
      ...options,
    });
  },

  getQuestionBank: async (questionId, options = {}) => {
    if (!questionId) throw new Error("questionId is required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.QUESTION_BANK_DETAIL(questionId),
      method: "GET",
      ...options,
    });
  },

  createQuestionBank: async (payload, options = {}) => {
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.QUESTION_BANK,
      method: "POST",
      body: payload,
      ...options,
    });
  },

  importStandardToBank: async (payload, options = {}) => {
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.QUESTION_BANK_IMPORT_STANDARD,
      method: "POST",
      body: payload,
      ...options,
    });
  },

  createDerivedQuestion: async (payload, options = {}) => {
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.QUESTION_BANK_DERIVE,
      method: "POST",
      body: payload,
      ...options,
    });
  },

  updateQuestionBank: async (questionId, payload, options = {}) => {
    if (!questionId) throw new Error("questionId is required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.QUESTION_BANK_DETAIL(questionId),
      method: "PATCH",
      body: payload,
      ...options,
    });
  },

  archiveQuestionBank: async (questionId, options = {}) => {
    if (!questionId) throw new Error("questionId is required");
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.QUESTION_BANK_ARCHIVE(questionId),
      method: "POST",
      body: {},
      ...options,
    });
  },

  submitSurveyResponse: async (payload, options = {}) => {
    return createApiRequest({
      url: SURVEYS_ENDPOINTS.RESPONSE,
      method: "POST",
      body: payload,
      ...options,
    });
  },

  deleteSurveyResponse: async (responseId, options = {}) => {
    if (!responseId) throw new Error("responseId is required");
    await createApiRequest({
      url: SURVEYS_ENDPOINTS.RESPONSE_DELETE(responseId),
      method: "DELETE",
      ...options,
    });
    return { success: true };
  },
};
